// src/PrivacyPage.jsx
import React from 'react';
import { openAnalyticsSettings } from './analytics';

export default function PrivacyPage({ onClose }) {
  return (
    <div style={{ minHeight: '100vh', background: '#fff', padding: '40px 20px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', color: '#1f2937', lineHeight: 1.6, fontSize: 15 }}>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#6b7280', fontSize: 14, cursor: 'pointer', marginBottom: 24, padding: 0 }}>
          ← Back
        </button>

        <h1 style={{ fontSize: 32, fontWeight: 800, marginBottom: 8 }}>Privacy Policy</h1>
        <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 32 }}>Last updated: 25 June 2026</p>

        <p>This Privacy Policy explains what data we collect, how we use it, and your rights under the EU General Data Protection Regulation (GDPR).</p>

        <h2 style={s.h2}>1. Who We Are</h2>
        <p>MakeMyResume ("we", "our") is operated as an individual sole trader. The data controller is Brian Kavanagh, based in Berlin, Germany. For any privacy questions, contact <a href="mailto:support@makemyresume.app" style={s.a}>support@makemyresume.app</a>.</p>

        <h2 style={s.h2}>2. What We Collect</h2>
        <p><strong>Account data</strong> (via Clerk): email address, name (if provided), authentication tokens. Required to identify you.</p>
        <p><strong>Subscription data</strong> (via Stripe): payment processor stores payment-method details; we store only your Stripe customer ID, subscription plan, and expiration date.</p>
        <p><strong>CV content</strong>: your CV data (personal info, experience, education, skills) is stored in local storage for this browser profile. It does not sync across devices or accounts. We do not send CV content to our servers <em>except</em> when you click an AI feature, in which case the relevant text is sent to Google Gemini for processing.</p>
        <p><strong>Usage data</strong>: we log daily AI feature usage per user (count only, no content) to enforce plan limits. If you opt in to analytics, we also record anonymized page views and a small set of feature events such as template selection and PDF export.</p>
        <p><strong>Technical data</strong>: standard server logs (IP address, browser type, request paths) for security and debugging. Retained for 30 days.</p>

        <h2 style={s.h2}>3. Why We Collect It (Lawful Basis)</h2>
        <ul style={s.ul}>
          <li><strong>Contract</strong>: to provide the Service you signed up for (Art. 6(1)(b) GDPR).</li>
          <li><strong>Legitimate interest</strong>: security and fraud prevention (Art. 6(1)(f)).</li>
          <li><strong>Consent</strong>: optional, anonymized product analytics. You can withdraw consent at any time using the analytics settings above.</li>
          <li><strong>Legal obligation</strong>: tax records for payments (Art. 6(1)(c)).</li>
        </ul>

        <h2 style={s.h2}>4. Third Parties (Data Processors)</h2>
        <p>We use these vetted services to operate. They process data only on our behalf, under data-processing agreements:</p>
        <ul style={s.ul}>
          <li><strong>Vercel</strong> (USA/EU) — hosting and data storage; anonymized analytics only if you opt in</li>
          <li><strong>Clerk</strong> (USA) — authentication</li>
          <li><strong>Stripe</strong> (Ireland for EU customers) — payment processing</li>
          <li><strong>Google Gemini</strong> (USA) — AI features (only when you click an AI button)</li>
          <li><strong>Upstash</strong> (USA/EU) — subscription database (Frankfurt region for EU)</li>
        </ul>
        <p>Where data is transferred outside the EU, we rely on Standard Contractual Clauses (SCCs) and the EU-US Data Privacy Framework where applicable.</p>

        <h2 style={s.h2}>5. AI Processing</h2>
        <p>When you use AI features, the relevant CV text is sent to Google Gemini API. Per Google's policy for paid API usage, this data is <strong>not</strong> used to train their models. We do not store the AI responses on our servers — they're returned directly to your browser.</p>

        <h2 style={s.h2}>6. Local Storage</h2>
        <p>Your CV content and saved versions live in localStorage for this browser profile, not on our servers. Drafts save automatically while you work, but are not available on another device or browser. Clearing this browser's site data will permanently delete them, so export a PDF or keep another copy if you need a backup. We cannot see your CV content unless you choose an AI feature, which sends the text needed for that request to Google Gemini.</p>

        <h2 style={s.h2}>7. Cookies</h2>
        <p>We use essential storage for authentication and security. If you opt in, Vercel Web Analytics records anonymized page views, referrer information, and selected feature events without cookies. We do not send your CV content, name, email address, or other account details to analytics. Your choice is stored locally in your browser and can be changed below.</p>
        <button onClick={openAnalyticsSettings} style={{ background: '#eef2ff', border: '1px solid #c7d2fe', color: '#4338ca', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontWeight: 650, cursor: 'pointer' }}>Change analytics choice</button>

        <h2 style={s.h2}>8. Your Rights Under GDPR</h2>
        <p>You have the right to:</p>
        <ul style={s.ul}>
          <li><strong>Access</strong> the personal data we hold about you</li>
          <li><strong>Correct</strong> inaccurate data</li>
          <li><strong>Delete</strong> your account and associated data ("right to be forgotten")</li>
          <li><strong>Export</strong> your data in a portable format</li>
          <li><strong>Object</strong> to processing or restrict it</li>
          <li><strong>Withdraw consent</strong> at any time</li>
          <li><strong>Lodge a complaint</strong> with a supervisory authority (e.g. Berlin BlnBDI for Germany-based users)</li>
        </ul>
        <p>To exercise any right, email <a href="mailto:support@makemyresume.app" style={s.a}>support@makemyresume.app</a>. We respond within 30 days.</p>

        <h2 style={s.h2}>9. Data Retention</h2>
        <ul style={s.ul}>
          <li><strong>Account data</strong>: kept while your account is active, deleted within 30 days of account closure (or extended for tax/legal reasons up to 7 years for payment records)</li>
          <li><strong>Subscription data</strong>: kept for legal/accounting reasons (10 years in Germany)</li>
          <li><strong>Usage logs</strong>: 30 days. Opt-in analytics is retained by Vercel according to the plan's reporting window.</li>
          <li><strong>CV content</strong>: never reaches our servers — only ever in your browser</li>
        </ul>

        <h2 style={s.h2}>10. Security</h2>
        <p>We use industry-standard security measures: HTTPS everywhere, encrypted storage, signed webhooks for payments. No system is 100% secure; you use the Service at your own risk.</p>

        <h2 style={s.h2}>11. Children</h2>
        <p>The Service is not directed at users under 16. We do not knowingly collect data from children.</p>

        <h2 style={s.h2}>12. Changes</h2>
        <p>We may update this Policy. Material changes will be announced on-site or via email. Continued use after changes implies acceptance.</p>

        <h2 style={s.h2}>13. Contact & Complaints</h2>
        <p>Questions or complaints: <a href="mailto:support@makemyresume.app" style={s.a}>support@makemyresume.app</a></p>
        <p>EU users may also contact their local data protection authority. For Germany: <a href="https://www.datenschutz-berlin.de" target="_blank" rel="noopener noreferrer" style={s.a}>Berliner Beauftragte für Datenschutz und Informationsfreiheit</a>.</p>

        <div style={{ height: 40 }}/>
      </div>
    </div>
  );
}

const s = {
  h2: { fontSize: 20, fontWeight: 700, marginTop: 32, marginBottom: 12, color: '#111827' },
  ul: { paddingLeft: 24, marginBottom: 16 },
  a: { color: '#2563eb', textDecoration: 'underline' }
};
